import { MILLION } from 'constants/dataSets';
import {
  CODE,
  CRITICALITY_FROM_DOWNSTREAM_WASTE,
  CRITICALITY_RATING,
  DATE_CLOSED,
  DATE_OPENED,
  NAME,
  PRODUCT_CATEGORY_ID,
  PRODUCT_TYPE,
  PROFIT_CENTER_LINKS,
  RELIANCE_ON_FREE_NATURAL_RESOURCES,
  RELIANCE_ON_FREE_SOCIAL_RESOURCES,
  SOURCING_CONNECTIONS,
  UNIQUE_IDENTIFIER,
  UPSTREAM_TIER,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
  VOLUME_QTY, VOLUME_QTY_UNIT,
} from 'constants/formFields';
import { DATA_SETS_DATE_FORMAT, DATE_FORMAT_ISO } from 'constants/general';
import dayjs from 'dayjs';
import { getProductTypeByValueChain } from 'utils/formHelpers/productTypeValueChain';
import { splitSourcingConnections } from 'utils/sourcingConnections';

import type IProfitCenterLink from 'store/slices/dataSets/interfaces/IProfitCenterLink';
import type { IGetRequestDataForProduct } from './interfaces/IGetRequestDataForProduct';

function getRequestDataForProductUpdate({
  values, recordId, companyCurrency, valueChainForm, includeUniqueIdentifier,
}: IGetRequestDataForProduct) {
  const getProfitCenterLinks = (
    profitCenterLinks: IProfitCenterLink[],
  ) => profitCenterLinks.map((profitCenterLink) => ({
    profitCenterId: profitCenterLink.profitCenterId || null,
    startDate: dayjs(profitCenterLink.startDate, DATA_SETS_DATE_FORMAT).format(DATE_FORMAT_ISO) || null,
    endDate: dayjs(profitCenterLink.endDate, DATA_SETS_DATE_FORMAT).format(DATE_FORMAT_ISO) || null,
    totalCost: {
      amount: profitCenterLink.totalCost ? Number(profitCenterLink.totalCost) * MILLION : null,
      currency: companyCurrency || null,
    },
    percentOfTotalCost: profitCenterLink.percentOfTotalCost || 0,
    expectedGrowthDecline: profitCenterLink.expectedGrowthDecline || 0,
  }));

  return {
    [NAME]: values?.[NAME] || null,
    [CODE]: values?.[CODE] || null,
    ...(!recordId || valueChainForm !== true ? {
      [VALUE_CHAIN_SECTION]: `${values?.[VALUE_CHAIN_LIST]?.value}. ${values?.[VALUE_CHAIN_SECTION]?.value}` || null,
    } : {}),
    [UPSTREAM_TIER]: values?.[UPSTREAM_TIER]?.value || null,
    // `supplierLinks` is deliberately absent: these endpoints derive it from the connections
    // below, and still sending it fails validation with a 400.
    //
    // Set semantics apply per array: an item without `id` creates an edge, any stored edge whose
    // id is missing is deleted, and an omitted key leaves that side alone entirely. The split
    // spreads to nothing when the list never hydrated, which is what keeps both keys off.
    ...splitSourcingConnections(values?.[SOURCING_CONNECTIONS]),
    [DATE_OPENED]: values?.[DATE_OPENED] ? dayjs(values?.[DATE_OPENED], DATA_SETS_DATE_FORMAT).format(DATE_FORMAT_ISO)
      : null,
    [DATE_CLOSED]: values?.[DATE_CLOSED] ? dayjs(values?.[DATE_CLOSED], DATA_SETS_DATE_FORMAT).format(DATE_FORMAT_ISO)
      : null,
    [VOLUME_QTY]: values?.[VOLUME_QTY] ? Number(values?.[VOLUME_QTY]) : null,
    [VOLUME_QTY_UNIT]: values?.[VOLUME_QTY_UNIT]?.value || null,
    [CRITICALITY_RATING]: values?.[CRITICALITY_RATING]?.value || null,
    [CRITICALITY_FROM_DOWNSTREAM_WASTE]: values?.[CRITICALITY_FROM_DOWNSTREAM_WASTE]
      ? Number(values?.[CRITICALITY_FROM_DOWNSTREAM_WASTE]) : null,
    [RELIANCE_ON_FREE_NATURAL_RESOURCES]:
        (values?.[RELIANCE_ON_FREE_NATURAL_RESOURCES]?.length && values?.[RELIANCE_ON_FREE_NATURAL_RESOURCES]?.map(
          (item) => item.value,
        )) || null,
    [RELIANCE_ON_FREE_SOCIAL_RESOURCES]:
        (values?.[RELIANCE_ON_FREE_SOCIAL_RESOURCES]?.length && values?.[RELIANCE_ON_FREE_SOCIAL_RESOURCES]?.map(
          (item) => item.value,
        )) || null,
    [PROFIT_CENTER_LINKS]: getProfitCenterLinks(values?.[PROFIT_CENTER_LINKS]),
    [PRODUCT_CATEGORY_ID]: Number(values?.[PRODUCT_CATEGORY_ID]?.value) || null,
    ...((!recordId || includeUniqueIdentifier) && { [UNIQUE_IDENTIFIER]: values?.[UNIQUE_IDENTIFIER] || null }),
    ...(!recordId && { [PRODUCT_TYPE]: getProductTypeByValueChain(values?.[VALUE_CHAIN_LIST]?.value) }),
    ...(recordId && { id: Number(recordId) }),
  };
}

export default getRequestDataForProductUpdate;
